import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  updateThesaurusGroupById as updateThesaurusGroupByIdAction,
  selectThesaurusGroupById as selectThesaurusGroupByIdAction,
  syncThesaurusItemsWithGrouops as syncThesaurusItemsWithGrouopsAction,
  clearAllSelectedItemsInThesaurus as clearAllSelectedItemsInThesaurusAction,
} from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersActions';
import { filter as filterSelector, thesaurusGroupsForViewFactory } from './TreeViewDialogSelectors';
import {
  limitTo as limitToAction,
  exclude as excludeAction,
  toggleGroup as toggleGroupAction,
  fetchChildren as fetchChildrenAction,
  toggleSearchOptions as toggleSearchOptionsAction,
} from './TreeViewDialogActions';
import TreeViewDialog from './TreeViewDialog';

export function TreeViewDialogContainer(props) {
  const {
    filter,
    filterId,
    expandSearchOptions,
    toggleSearchOptions,
    keyifyParamsForFiltersRequest,
    selectThesaurusGroupById,
    toggleGroup,
    openCloseMap,
    fetchChildren,
    clearAllSelectedItemsInThesaurus,
    selfClose,
    limitTo,
    exclude,
    tags,
    syncThesaurusItemsWithGrouops,
  } = props;

  // Handlers
  const onToggleSearchOptions = useCallback(() => {
    toggleSearchOptions(filterId, keyifyParamsForFiltersRequest, !expandSearchOptions);
  }, [filterId, keyifyParamsForFiltersRequest, expandSearchOptions]);
  const onClickThesaurusItem = useCallback(
    (item) => {
      const groupId = item.get('id');
      selectThesaurusGroupById(keyifyParamsForFiltersRequest, filterId, groupId);
    },
    [filterId, keyifyParamsForFiltersRequest]
  );
  const onToggelRootGroup = useCallback(() => {
    const thesaurusRootId = filter.get('thesaurusRootId');

    toggleGroup(filterId, keyifyParamsForFiltersRequest, thesaurusRootId, !openCloseMap[thesaurusRootId], openCloseMap);
  }, [filter, filterId, keyifyParamsForFiltersRequest, openCloseMap]);
  const onToggleGroup = useCallback(
    (item) => {
      const childrenRetrievalStatus = item.get('childrenRetrievalStatus');
      const id = item.get('rootIds', List()).push(item.get('id')).join();

      toggleGroup(filterId, keyifyParamsForFiltersRequest, id, !openCloseMap[id], openCloseMap);

      if (childrenRetrievalStatus === 'fetched' || childrenRetrievalStatus === 'fetching') {
        return;
      }

      fetchChildren(filter, item);
    },
    [filter, filterId, keyifyParamsForFiltersRequest, openCloseMap]
  );
  const onClickClearAllSelected = useCallback(() => {
    clearAllSelectedItemsInThesaurus(keyifyParamsForFiltersRequest, filterId);
  }, []);
  const onClickLimitTo = useCallback(() => {
    selfClose();
    limitTo(filter, tags);
  }, [filter, tags]);
  const onClickExclude = useCallback(() => {
    selfClose();
    exclude(filter, tags);
  }, [filter, tags]);

  // Effects
  useEffect(() => {
    syncThesaurusItemsWithGrouops(keyifyParamsForFiltersRequest, filterId);
  }, []);

  useDidUpdateEffect(() => {
    const tagsCount = tags.count();

    if (!expandSearchOptions) {
      return;
    }

    if (tagsCount > 1) {
      return;
    }

    toggleSearchOptions(filterId, keyifyParamsForFiltersRequest, false);
  }, [tags, expandSearchOptions]);

  return (
    <TreeViewDialog
      {...props}
      onToggleSearchOptions={onToggleSearchOptions}
      onClickThesaurusItem={onClickThesaurusItem}
      onToggelRootGroup={onToggelRootGroup}
      onToggleGroup={onToggleGroup}
      onClickClearAllSelected={onClickClearAllSelected}
      onClickLimitTo={onClickLimitTo}
      onClickExclude={onClickExclude}
    />
  );
}

TreeViewDialogContainer.propTypes = {
  filter: PropTypes.instanceOf(Map),
  filterId: PropTypes.string,
  expandSearchOptions: PropTypes.bool,
  toggleSearchOptions: PropTypes.func,
  keyifyParamsForFiltersRequest: PropTypes.string,
  selectThesaurusGroupById: PropTypes.func,
  toggleGroup: PropTypes.func,
  openCloseMap: PropTypes.object,
  fetchChildren: PropTypes.func,
  clearAllSelectedItemsInThesaurus: PropTypes.func,
  selfClose: PropTypes.func,
  limitTo: PropTypes.func,
  exclude: PropTypes.func,
  tags: PropTypes.instanceOf(List),
  syncThesaurusItemsWithGrouops: PropTypes.func,
};

function mapStateToProps() {
  const { tags, visibleItems, selectedCount, thesaurusGroupsForView, thesaurusGroupsPartiallySelectedMap } =
    thesaurusGroupsForViewFactory();

  return createStructuredSelector({
    tags,
    filter: filterSelector,
    visibleItems,
    selectedCount,
    thesaurusGroupsForView,
    thesaurusGroupsPartiallySelectedMap,
  });
}

const mapDispatchToProps = {
  limitTo: limitToAction,
  exclude: excludeAction,
  toggleGroup: toggleGroupAction,
  fetchChildren: fetchChildrenAction,
  toggleSearchOptions: toggleSearchOptionsAction,
  updateThesaurusGroupById: updateThesaurusGroupByIdAction,
  selectThesaurusGroupById: selectThesaurusGroupByIdAction,
  syncThesaurusItemsWithGrouops: syncThesaurusItemsWithGrouopsAction,
  clearAllSelectedItemsInThesaurus: clearAllSelectedItemsInThesaurusAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(TreeViewDialogContainer);
